import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import * as GroupConfidentialityDeedActions from "../../../actions/groupConfidentialityDeed";
import * as GroupContractActions from "../../../actions/groupContract";
import * as NavigationActions from "../../../actions/navigation";
import {
    InlineLoading,
    Modal,
    NotificationAlert,
} from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    SelectFormInput,
    TextFormInput,
} from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { GroupContractBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../../helpers/date";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { GroupSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IGroupConfidentialityDeedReduxFormState } from "../../../reducers/reactReduxForms/groupConfidentialityDeed";
import { IGroupContractReduxFormState } from "../../../reducers/reactReduxForms/groupContract";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./GroupContractPage.scss";

interface GroupContractPageProps {
    // Group Contract
    form: Dtos.GroupContract & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IGroupContractReduxFormState;
    groupContract: Dtos.GroupContract;
    lookups: Dtos.Lookup[];
    loadingGroupContract: boolean;
    loadGroupContractSuccess: boolean;
    loadGroupContractFailure: boolean;
    loadGroupContractErrorCode: string;
    savingGroupContract: boolean;
    saveGroupContractSuccess: boolean;
    saveGroupContractFailure: boolean;
    validatingGroupContract: boolean;
    validationFailures: Dtos.ResponseError[];
    clearGroupContract: GroupContractActions.IGroupContractClearActionCreator;
    loadGroupContract: GroupContractActions.IGroupContractLoadByGroupIdActionCreator;
    saveGroupContract: GroupContractActions.IGroupContractSaveActionCreator;
    validateGroupContract: GroupContractActions.IGroupContractValidateActionCreator;

    // Group Confidentiality Deed
    groupConfidentialityDeedForm: Dtos.GroupConfidentialityDeed & IRegistrationFormState;
    groupConfidentialityDeedFormState: Dtos.FormState;
    groupConfidentialityDeedFormProperties: Dtos.FormProperty[];
    groupConfidentialityDeedReduxFormState: IGroupConfidentialityDeedReduxFormState;
    groupConfidentialityDeed: Dtos.GroupConfidentialityDeed;
    groupConfidentialityDeedLookups: Dtos.Lookup[];
    loadingGroupConfidentialityDeed: boolean;
    loadGroupConfidentialityDeedSuccess: boolean;
    loadGroupConfidentialityDeedFailure: boolean;
    savingGroupConfidentialityDeed: boolean;
    saveGroupConfidentialityDeedSuccess: boolean;
    saveGroupConfidentialityDeedFailure: boolean;
    validatingGroupConfidentialityDeed: boolean;
    creatingGroupConfidentialityDeed: boolean;
    createGroupConfidentialityDeedSuccess: boolean;
    createGroupConfidentialityDeedFailure: boolean;
    deletingGroupConfidentialityDeed: boolean;
    deleteGroupConfidentialityDeedSuccess: boolean;
    groupConfidentialityDeedValidationFailures: Dtos.ResponseError[];
    clearGroupConfidentialityDeed: GroupConfidentialityDeedActions.IGroupConfidentialityDeedClearActionCreator;
    loadGroupConfidentialityDeed: GroupConfidentialityDeedActions.IGroupConfidentialityDeedLoadByIdActionCreator;
    saveGroupConfidentialityDeed: GroupConfidentialityDeedActions.IGroupConfidentialityDeedSaveActionCreator;
    createGroupConfidentialityDeed: GroupConfidentialityDeedActions.IGroupConfidentialityDeedCreateActionCreator;
    deleteGroupConfidentialityDeed: GroupConfidentialityDeedActions.IGroupConfidentialityDeedDeleteActionCreator;
    validateGroupConfidentialityDeed: GroupConfidentialityDeedActions.IGroupConfidentialityDeedValidateActionCreator;

    // Common
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

}

const reduxFormName: string = "reduxForms.groupContract";
const groupConfidentialityDeedReduxFormName: string = "reduxForms.groupConfidentialityDeed";

const GroupContractPage: FC<GroupContractPageProps> = ({
    form,
    formState,
    formProperties,
    groupContract,
    lookups,
    loadingGroupContract,
    loadGroupContractFailure,
    loadGroupContractErrorCode,
    savingGroupContract,
    saveGroupContractSuccess,
    saveGroupContractFailure,
    validatingGroupContract,
    validationFailures,
    clearGroupContract,
    loadGroupContract,
    saveGroupContract,
    validateGroupContract,

    groupConfidentialityDeedForm,
    groupConfidentialityDeedFormState,
    groupConfidentialityDeedFormProperties,
    groupConfidentialityDeed,
    groupConfidentialityDeedLookups,
    loadingGroupConfidentialityDeed,
    savingGroupConfidentialityDeed,
    saveGroupConfidentialityDeedSuccess,
    saveGroupConfidentialityDeedFailure,
    creatingGroupConfidentialityDeed,
    deletingGroupConfidentialityDeed,
    deleteGroupConfidentialityDeedSuccess,
    validatingGroupConfidentialityDeed,
    groupConfidentialityDeedValidationFailures,
    clearGroupConfidentialityDeed,
    loadGroupConfidentialityDeed,
    saveGroupConfidentialityDeed,
    createGroupConfidentialityDeed,
    deleteGroupConfidentialityDeed,
    validateGroupConfidentialityDeed,

    permissions,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [groupConfidentialityDeedId, setGroupConfidentialityDeedId] = useState<number | null>(null);
    const [formInitalisedGroupConfidentialityDeed, setFormInitalisedGroupConfidentialityDeed] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [hasBeenSubmittedConfidentalityDeed, setHasBeenSubmittedConfidentalityDeed] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.GroupContract = {} as Dtos.GroupContract;

        loadForm(reduxFormName, Object.assign({ ...groupContract }, extraData));
    };

    const loadGroupConfidentialityDeedReduxForm = () => {
        let extraData: Dtos.GroupConfidentialityDeed =
            {} as Dtos.GroupConfidentialityDeed;

        loadForm(
            groupConfidentialityDeedReduxFormName,
            Object.assign({ ...groupConfidentialityDeed }, extraData)
        );
    };


    // Load Group Contract
    useEffect(() => {
        if (groupIdNumber && !groupContract) {
            setBreadcrumbs(GroupContractBreadcrumbs(groupIdNumber));
            setDrawerItems(GroupSubMenu(groupIdNumber, permissions));

            loadGroupContract(groupIdNumber);
        } else if (groupContract && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        groupIdNumber,
        groupContract,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadGroupContract,
        GroupContractBreadcrumbs,
        setFormInitalised,
    ]);

    // Load Group Confidentiality Deed
    useEffect(() => {
        if (isOpen) {
            if (
                groupConfidentialityDeedId &&
                !groupConfidentialityDeed &&
                !loadingGroupConfidentialityDeed
            ) {
                loadGroupConfidentialityDeed(groupConfidentialityDeedId);
            } else if (
                !groupConfidentialityDeedId &&
                !groupConfidentialityDeed &&
                !creatingGroupConfidentialityDeed
            ) {
                createGroupConfidentialityDeed(groupIdNumber);
            } else if (
                groupConfidentialityDeed &&
                !formInitalisedGroupConfidentialityDeed
            ) {
                loadGroupConfidentialityDeedReduxForm();
                setFormInitalisedGroupConfidentialityDeed(true);
            }
        }
    }, [
        isOpen,
        groupConfidentialityDeedId,
        groupConfidentialityDeed,
        formInitalisedGroupConfidentialityDeed,
        loadGroupConfidentialityDeed,
        setFormInitalisedGroupConfidentialityDeed,
        loadGroupConfidentialityDeedReduxForm,
        loadingGroupConfidentialityDeed,
        groupIdNumber,
        createGroupConfidentialityDeed,
        creatingGroupConfidentialityDeed,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupContractFailure) {
            navigate(`/error/${loadGroupContractErrorCode}`);
            clearGroupContract();
            clearGroupConfidentialityDeed();
        }
    }, [
        loadGroupContractFailure,
        loadGroupContractErrorCode,
        navigate,
        clearGroupContract,
        clearGroupConfidentialityDeed,
    ]);

    // Reload group contract & confidentiality deed after delete
    useEffect(() => {
        if (
            !deletingGroupConfidentialityDeed &&
            deleteGroupConfidentialityDeedSuccess
        ) {
            clearGroupContract();
            resetForm(reduxFormName);
            setFormInitalised(false);
            clearGroupConfidentialityDeed();
            setGroupConfidentialityDeedId(null);
            setIsOpen(false);
            resetForm(groupConfidentialityDeedReduxFormName);
            setFormInitalisedGroupConfidentialityDeed(false);
        }
    }, [
        deletingGroupConfidentialityDeed,
        deleteGroupConfidentialityDeedSuccess,
        loadGroupConfidentialityDeed,
        loadGroupContract,
        groupIdNumber,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (groupIdNumber && groupContract?.group) {
            setBreadcrumbs(
                GroupContractBreadcrumbs(
                    groupIdNumber,
                    groupContract.group?.name
                )
            );
        }
    }, [
        groupIdNumber,
        groupContract,
        setBreadcrumbs,
        GroupContractBreadcrumbs,
    ]);

    // Reload group contract after saving
    useEffect(() => {
        if (!savingGroupContract && saveGroupContractSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Group contract saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/groups`);
            } else if (!groupIdNumber) {
                navigate(`/groups/${groupIdNumber}/contract`);
                clearGroupContract();
            }
        }
    }, [
        savingGroupContract,
        saveGroupContractSuccess,
        groupIdNumber,
        navigate,
        saveAndReturn,
        clearGroupContract,
    ]);

    // Failed saving group contract
    useEffect(() => {
        if (!savingGroupContract && saveGroupContractFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingGroupContract, saveGroupContractSuccess]);

    // Reload group confidentiality after saving
    useEffect(() => {
        if (
            !savingGroupConfidentialityDeed &&
            saveGroupConfidentialityDeedSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Group confidentiality saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            clearGroupContract();
            resetForm(reduxFormName);
            setFormInitalised(false);
            clearGroupConfidentialityDeed();
            setGroupConfidentialityDeedId(null);
            setIsOpen(false);
            resetForm(groupConfidentialityDeedReduxFormName);
            setFormInitalisedGroupConfidentialityDeed(false);
        }
    }, [
        savingGroupConfidentialityDeed,
        saveGroupConfidentialityDeedSuccess,
        setIsOpen,
        clearGroupContract,
        clearGroupConfidentialityDeed,
        setGroupConfidentialityDeedId,
        resetForm,
        reduxFormName,
        groupConfidentialityDeedReduxFormName,
    ]);

    // Failed saving group confidentiality deed
    useEffect(() => {
        if (
            !savingGroupConfidentialityDeed &&
            saveGroupConfidentialityDeedFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingGroupConfidentialityDeed, saveGroupConfidentialityDeedFailure]);

    // Cleanup effect to clear the group contract and confidentiality deed on component unmount
    useEffect(() => {
        return () => {
            clearGroupContract();
            clearGroupConfidentialityDeed();
            resetForm(reduxFormName);
            resetForm(groupConfidentialityDeedReduxFormName);
        };
    }, [
        clearGroupContract,
        clearGroupConfidentialityDeed,
        resetForm,
        reduxFormName,
        groupConfidentialityDeedReduxFormName,
    ]);

    // Group contract submit
    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.GroupContract = {} as Dtos.GroupContract;

            if (!data.groupId) {
                changes.groupId = groupIdNumber;
            }

            await saveGroupContract(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Group contract submit failed
    const handleOnSubmitFailed = (_data: Dtos.GroupContract) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    // Group confidentiality deed submit
    const handleOnSubmitGroupConfidentialityDeed = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmittedConfidentalityDeed(true);
        }

        if (data) {
            let changes: Dtos.GroupConfidentialityDeed = {} as Dtos.GroupConfidentialityDeed;

            if (!data.groupId) {
                changes.groupId = groupIdNumber;
            }

            await saveGroupConfidentialityDeed(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Group confidentiality deed submit failed
    const handleOnSubmitFailedGroupConfidentialityDeed = (_data: Dtos.GroupConfidentialityDeed) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate("/groups");
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.GroupContract = {} as Dtos.GroupContract;

            if (form && hasBeenSubmitted && !validatingGroupContract) {
                await validateGroupContract(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidateConfidentialityDeed = debounce(async () => {
            let changes: Dtos.GroupConfidentialityDeed = {} as Dtos.GroupConfidentialityDeed;

            if (
                groupConfidentialityDeedForm &&
                hasBeenSubmittedConfidentalityDeed &&
                !validatingGroupConfidentialityDeed
            ) {
                await validateGroupConfidentialityDeed(
                    Object.assign({ ...groupConfidentialityDeedForm }, changes)
                );
            }
        }, 250);

        debounceValidateConfidentialityDeed();

        return () => {
            debounceValidateConfidentialityDeed.cancel();
        };
    }, [groupConfidentialityDeedForm]);

    const openLink = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const generateDeleteButton = () => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.GroupConfidentialityDeedUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`groupConfidentialityDeed-DeleteButton-${groupContract?.groupConfidentialityDeed?.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setOpenDeleteModal(true);
                }}
            >
                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.TRASH} fixedWidth />
            </button>
        );
    };

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.GroupConfidentialityDeedUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openDeleteModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenDeleteModal(false);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenDeleteModal(false);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the confidentiality
                            deed "
                            <em>
                                {groupContract?.groupConfidentialityDeed?.name}"
                            </em>{" "}
                            from group{" "}
                            <strong>{groupContract?.group?.name}</strong>.
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteGroupConfidentialityDeed(
                                    groupContract?.groupConfidentialityDeed?.id
                                );
                                setOpenDeleteModal(false);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenDeleteModal(false);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        openDeleteModal,
        permissions,
        setOpenDeleteModal,
        groupIdNumber,
        groupConfidentialityDeed,
        groupContract,
        deleteGroupConfidentialityDeed,
    ]);

    const renderConfidentialityDeedModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [
                Dtos.Permission.GroupConfidentialityDeedUpdate,
                Dtos.Permission.GroupConfidentialityDeedCreate,
            ]
        );

        if (hasPermission) {
            const formDisabled: boolean =
                !hasPermission ||
                savingGroupConfidentialityDeed ||
                (groupConfidentialityDeedFormState &&
                    groupConfidentialityDeedFormState.formStatus ==
                        Dtos.FormStatus.Disabled);

            let formComponents: { [index: string]: RegistrationFormComponent } =
                {
                    detailsGroup: {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <>
                                <div className="form-row pt-4 pb-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            groupConfidentialityDeedForm,
                                            "groupConfidentialityDeed",
                                            {
                                                name: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                    fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                                },
                                            },
                                            groupConfidentialityDeedLookups,
                                            groupConfidentialityDeedValidationFailures,
                                            groupConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            groupConfidentialityDeedForm,
                                            "groupConfidentialityDeed",
                                            {
                                                location: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        addOnRightButton: (
                                                            <button
                                                                className="btn btn-secondary"
                                                                type="button"
                                                                id="button-addon2"
                                                                disabled={groupConfidentialityDeedForm?.location == null || !isUrl(groupConfidentialityDeedForm?.location)}
                                                                style={{
                                                                    cursor: groupConfidentialityDeedForm?.location == null || !isUrl(groupConfidentialityDeedForm?.location)
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                    padding: "4px 10px",
                                                                }}
                                                                onClick={() => {
                                                                    openLink(groupConfidentialityDeedForm.location);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                            </button>
                                                        ),
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                    fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                                },
                                            },
                                            groupConfidentialityDeedLookups,
                                            groupConfidentialityDeedValidationFailures,
                                            groupConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row pt-2 pb-4 px-4">
                                    <div className="col-12 col-xl-6">
                                        {renderFormControls(
                                            groupConfidentialityDeedForm,
                                            "groupConfidentialityDeed",
                                            {
                                                dateOfDeed: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: DatePickerFormInputFabric,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                    fieldClassName: "col-sm-12 col-xl-7 generic-form-field",
                                                },
                                            },
                                            groupConfidentialityDeedLookups,
                                            groupConfidentialityDeedValidationFailures,
                                            groupConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                    <div className="col-12 col-xl-6">
                                        {renderFormControls(
                                            groupConfidentialityDeedForm,
                                            "groupConfidentialityDeed",
                                            {
                                                dateFullyExecuted: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: DatePickerFormInputFabric,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                    fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                                },
                                            },
                                            groupConfidentialityDeedLookups,
                                            groupConfidentialityDeedValidationFailures,
                                            groupConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                            </>
                        ),
                    },
                };

            return (
                <Modal
                    open={isOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setIsOpen(false);
                        setGroupConfidentialityDeedId(null);
                        clearGroupConfidentialityDeed();
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <h2 className="mb-0">Confidentiality Deed</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setIsOpen(false);
                                setGroupConfidentialityDeedId(null);
                                clearGroupConfidentialityDeed();
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body" style={{ minHeight: "280px" }}>
                        {loadingGroupConfidentialityDeed || creatingGroupConfidentialityDeed ? (
                            <div className="d-flex h-100 align-items-center justify-content-center">
                                <InlineLoading />
                            </div>
                        ) : (
                            <Form
                                model={groupConfidentialityDeedReduxFormName}
                                onSubmit={(val, _event) =>
                                    handleOnSubmitGroupConfidentialityDeed(val)
                                }
                                onSubmitFailed={(val) =>
                                    handleOnSubmitFailedGroupConfidentialityDeed(val)
                                }
                            >
                                {renderFormControls(
                                    groupConfidentialityDeedForm,
                                    "groupConfidentialityDeed",
                                    formComponents,
                                    groupConfidentialityDeedLookups,
                                    groupConfidentialityDeedValidationFailures,
                                    groupConfidentialityDeedFormProperties,
                                    changeForm
                                )}
                                <div className="mt-4 mb-4 text-right">
                                    <button
                                        type="submit"
                                        disabled={formDisabled}
                                        className="btn btn-primary mr-2"
                                        onClick={() => {
                                            changeForm(
                                                groupConfidentialityDeedReduxFormName + ".saveAndReturn",
                                                false
                                            );
                                        }}
                                    >
                                        Submit
                                    </button>
                                    <button
                                        className="btn btn-primary mr-2"
                                        onClick={() => {
                                            setIsOpen(false);
                                            setGroupConfidentialityDeedId(null);
                                            clearGroupConfidentialityDeed();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Form>
                        )}
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        isOpen,
        permissions,
        setIsOpen,
        loadingGroupConfidentialityDeed,
        groupConfidentialityDeedReduxFormName,
        handleOnSubmitGroupConfidentialityDeed,
        handleOnSubmitFailedGroupConfidentialityDeed,
        savingGroupConfidentialityDeed,
        groupConfidentialityDeedFormState,
        groupConfidentialityDeedForm,
        openLink,
        setGroupConfidentialityDeedId,
        clearGroupConfidentialityDeed,
        creatingGroupConfidentialityDeed,
        changeForm,
        groupConfidentialityDeedFormProperties,
        groupConfidentialityDeedValidationFailures,
        groupConfidentialityDeedLookups,
    ]);

    const renderConfidentialityDeedContent = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [
                Dtos.Permission.GroupConfidentialityDeedUpdate,
                Dtos.Permission.GroupConfidentialityDeedCreate,
            ]
        );

        if (
            form?.groupConfidentialityDeed &&
            form?.groupConfidentialityDeed?.id
        ) {
            return (
                <div className="card card-shadow" style={{ zIndex: 1 }}>
                    <div className="table-wrapper">
                        <ReactTable
                            data={[form.groupConfidentialityDeed]}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            defaultPageSize={1}
                            pageSize={1}
                            showPagination={false}
                            className="-striped -highlight -clickable"
                            noDataText={`${form?.group?.name} has no confidentiality deed to view.`}
                            resizable={false}
                            onPageSizeChange={(_val) => {}}
                            getTdProps={(
                                _state,
                                rowInfo: RowInfo,
                                _column,
                                _instance
                            ) => {
                                return {
                                    onClick: (_e, handleOriginal) => {
                                        if (hasPermission) {
                                            setIsOpen(true);
                                            setGroupConfidentialityDeedId(rowInfo.original.id);
                                        }

                                        if (handleOriginal) {
                                            handleOriginal();
                                        }
                                    },
                                    style: {
                                        color:
                                            rowInfo &&
                                            rowInfo.original &&
                                            rowInfo.original.active
                                                ? "#212529"
                                                : "#9e9e9e",
                                    },
                                };
                            }}
                            defaultSorted={[
                                {
                                    id: "id",
                                    desc: false,
                                },
                            ]}
                            columns={[
                                {
                                    id: "id",
                                    Header: " ",
                                    accessor: "id",
                                    maxWidth: 50,
                                    Cell: (_props) => (
                                        <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.SIGNATURE} fixedWidth />
                                    ),
                                },
                                {
                                    id: "name",
                                    Header: "Name",
                                    accessor: "name",
                                },
                                {
                                    id: "dateOfDeed",
                                    Header: "Date Of Deed",
                                    accessor: "dateOfDeed",
                                    maxWidth: 250,
                                    Cell: (props) => (
                                        <div>
                                            <span
                                                data-tip={convertToApproximateDateString(props.value)}
                                            >
                                                {convertToShortDateString(props.value)}
                                            </span>
                                            <ReactTooltip />
                                        </div>
                                    ),
                                },
                                {
                                    id: "dateFullyExecuted",
                                    Header: "Date Fully Executed",
                                    accessor: "dateFullyExecuted",
                                    maxWidth: 250,
                                    Cell: (props) => (
                                        <div>
                                            <span
                                                data-tip={convertToApproximateDateString(props.value)}
                                            >
                                                {convertToShortDateString(props.value)}
                                            </span>
                                            <ReactTooltip />
                                        </div>
                                    ),
                                },
                                {
                                    id: "delete",
                                    Header: " ",
                                    maxWidth: 75,
                                    Cell: (_props) => (
                                        <div>{generateDeleteButton()}</div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="card card-shadow p-3" style={{ zIndex: 1 }}>
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                        </div>
                        <span>
                            <i>{form?.group?.name}</i> has no confidentiality deed to view.
                        </span>
                    </div>
                    {hasPermission && (
                        <div className="mt-3 d-flex justify-content-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsOpen(true);
                                }}
                                className="btn btn-primary"
                            >
                                Add
                            </button>
                        </div>
                    )}
                </div>
            );
        }
    }, [
        form,
        setIsOpen,
        PAGE_SIZE_OPTIONS,
        convertToApproximateDateString,
        convertToShortDateString,
        setGroupConfidentialityDeedId,
        permissions,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                groupIdNumber,
                null,
                permissions,
                Dtos.Permission.GroupContractUpdate,
                Dtos.Permission.GroupContractCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingGroupContract ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "groupContract",
                                        {
                                            agreementName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "groupContract",
                                        {
                                            noOfOriginalCTRARequired: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "groupContract",
                                        {
                                            groupContractSiteTypeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "GroupContract",
                                                                "groupContractSiteTypeId",
                                                                lookups
                                                            ),
                                                            form.groupContractSiteTypeId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            confidentialityDeedGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Confidentiality Deed</h1>
                        {renderConfidentialityDeedContent}
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "groupContract",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        type="submit"
                        className="btn btn-primary mr-2"
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                    >
                        Submit
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary mr-2"
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                    >
                        Submit And Return
                    </button>
                    <button className="btn btn-primary mr-2" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingGroupContract,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        groupContract,
        renderConfidentialityDeedContent,
        groupIdNumber,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingGroupContract || savingGroupContract}
        >
            {renderContent}
            {renderConfidentialityDeedModal}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<GroupContractPageProps> => {
    let groupContract: Dtos.GroupContract | undefined = !(
        state.groupContract.formData instanceof Array
    )
        ? state.groupContract.formData
        : undefined;
    let groupConfidentialityDeed: Dtos.GroupConfidentialityDeed | undefined = !(
        state.groupConfidentialityDeed.formData instanceof Array
    )
        ? state.groupConfidentialityDeed.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        groupContract: groupContract!,
        loadingGroupContract: state.groupContract.loadState && state.groupContract.loadState.status == RequestState.Pending,
        loadGroupContractSuccess: state.groupContract.loadState && state.groupContract.loadState.status == RequestState.Success,
        loadGroupContractFailure: state.groupContract.loadState && state.groupContract.loadState.status == RequestState.Failure,
        loadGroupContractErrorCode: state.groupContract.loadState.errorCode,
        savingGroupContract: state.groupContract.saveState && state.groupContract.saveState.status == RequestState.Pending,
        saveGroupContractSuccess: state.groupContract.saveState && state.groupContract.saveState.status == RequestState.Success,
        saveGroupContractFailure: state.groupContract.saveState && state.groupContract.saveState.status == RequestState.Failure,
        validatingGroupContract: state.groupContract.validateState && state.groupContract.validateState.status == RequestState.Pending,
        lookups: state.groupContract.lookups,
        form: state.reduxForms.groupContract,
        formState: state.groupContract.formState,
        formProperties: state.groupContract.formProperties,
        reduxFormState: state.reduxForms.formState.groupContract,
        validationFailures: groupContract
            ? state.groupContract.validationFailures
            : undefined,

        groupConfidentialityDeed: groupConfidentialityDeed!,
        loadingGroupConfidentialityDeed: state.groupConfidentialityDeed.loadState && state.groupConfidentialityDeed.loadState.status == RequestState.Pending,
        loadGroupConfidentialityDeedSuccess: state.groupConfidentialityDeed.loadState && state.groupConfidentialityDeed.loadState.status == RequestState.Success,
        loadGroupConfidentialityDeedFailure: state.groupConfidentialityDeed.loadState && state.groupConfidentialityDeed.loadState.status == RequestState.Failure,
        savingGroupConfidentialityDeed: state.groupConfidentialityDeed.saveState && state.groupConfidentialityDeed.saveState.status == RequestState.Pending,
        saveGroupConfidentialityDeedSuccess: state.groupConfidentialityDeed.saveState && state.groupConfidentialityDeed.saveState.status == RequestState.Success,
        saveGroupConfidentialityDeedFailure: state.groupConfidentialityDeed.saveState && state.groupConfidentialityDeed.saveState.status == RequestState.Failure,
        validatingGroupConfidentialityDeed: state.groupConfidentialityDeed.validateState && state.groupConfidentialityDeed.validateState.status == RequestState.Pending,
        creatingGroupConfidentialityDeed: state.groupConfidentialityDeed.createState && state.groupConfidentialityDeed.createState.status == RequestState.Pending,
        createGroupConfidentialityDeedSuccess: state.groupConfidentialityDeed.createState && state.groupConfidentialityDeed.createState.status == RequestState.Success,
        createGroupConfidentialityDeedFailure: state.groupConfidentialityDeed.createState && state.groupConfidentialityDeed.createState.status == RequestState.Failure,
        deletingGroupConfidentialityDeed: state.groupConfidentialityDeed.deleteState && state.groupConfidentialityDeed.deleteState.status == RequestState.Pending,
        deleteGroupConfidentialityDeedSuccess: state.groupConfidentialityDeed.deleteState && state.groupConfidentialityDeed.deleteState.status == RequestState.Success,
        groupConfidentialityDeedLookups: state.groupConfidentialityDeed.lookups,
        groupConfidentialityDeedForm: state.reduxForms.groupConfidentialityDeed,
        groupConfidentialityDeedFormState: state.groupConfidentialityDeed.formState,
        groupConfidentialityDeedFormProperties: state.groupConfidentialityDeed.formProperties,
        groupConfidentialityDeedReduxFormState: state.reduxForms.formState.groupConfidentialityDeed,
        groupConfidentialityDeedValidationFailures: groupConfidentialityDeed
            ? state.groupConfidentialityDeed.validationFailures
            : undefined,

        user: state.user.data,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<GroupContractPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),

        loadGroupContract: bindActionCreators(GroupContractActions.LoadGroupContractByGroupId, dispatch),
        clearGroupContract: bindActionCreators(GroupContractActions.Clear, dispatch),
        saveGroupContract: bindActionCreators(GroupContractActions.SaveGroupContract, dispatch),
        validateGroupContract: bindActionCreators(GroupContractActions.ValidateGroupContract, dispatch),

        loadGroupConfidentialityDeed: bindActionCreators(GroupConfidentialityDeedActions.LoadGroupConfidentialityDeedById, dispatch),
        clearGroupConfidentialityDeed: bindActionCreators(GroupConfidentialityDeedActions.Clear, dispatch),
        saveGroupConfidentialityDeed: bindActionCreators(GroupConfidentialityDeedActions.SaveGroupConfidentialityDeed, dispatch),
        createGroupConfidentialityDeed: bindActionCreators(GroupConfidentialityDeedActions.CreateGroupConfidentialityDeed, dispatch),
        deleteGroupConfidentialityDeed: bindActionCreators(GroupConfidentialityDeedActions.DeleteGroupConfidentialityDeed, dispatch),
        validateGroupConfidentialityDeed: bindActionCreators(GroupConfidentialityDeedActions.ValidateGroupConfidentialityDeed, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupContractPage);
